import { Building2, Check, LockKeyhole, Users } from "lucide-react";
import { useId, useState, type FormEvent, type JSX } from "react";

import { frontendConfig } from "../api/config.js";
import { BrandLogo } from "../components/BrandLogo.js";
import { PrimaryButton } from "../components/PrimaryButton.js";
import { useAuth } from "./AuthProvider.js";

export function WelcomePage(): JSX.Element {
  const auth = useAuth();
  const microsoftIdp = frontendConfig().oidcMicrosoftIdp;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const passwordId = useId();

  async function start(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch {
      setError("Die Anmeldung ist gerade nicht erreichbar. Bitte versuche es erneut.");
    } finally {
      setBusy(false);
    }
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void start(async () => {
      const result = await auth.signInWithPassword(email.trim(), password);
      if (result === "invalid-credentials") {
        setPassword("");
        setError(
          microsoftIdp
            ? "E-Mail-Adresse oder Passwort stimmen nicht. Konten aus Microsoft 365 melden sich über „Mit Microsoft anmelden“ an."
            : "E-Mail-Adresse oder Passwort stimmen nicht.",
        );
      }
    });
  };

  return (
    <main className="grid min-h-full bg-surface-sunken lg:grid-cols-[minmax(0,1.05fr)_minmax(28rem,0.95fr)]">
      <section className="relative hidden overflow-hidden bg-[#102441] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand/30 blur-3xl" />
        <div className="relative flex items-center gap-3"><BrandLogo className="h-16 w-16" /><span className="text-xl font-black tracking-tight">buma</span></div>
        <div className="relative max-w-xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-200">Ein Arbeitsplatz. Alle Werkzeuge.</p>
          <h1 className="mt-4 text-5xl font-black leading-[1.08] tracking-tight">Dein Unternehmen, übersichtlich an einem Ort.</h1>
          <p className="mt-5 max-w-lg text-lg leading-8 text-blue-100/80">Kommunikation, Wissen, Projekte, Dateien und Termine für Menschen und virtuelle Mitarbeiter.</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <Benefit icon={<Users />} text="Gemeinsam arbeiten" />
            <Benefit icon={<Building2 />} text="Mandantenfähig" />
            <Benefit icon={<LockKeyhole />} text="Sicher über Unternehmens-SSO" />
            <Benefit icon={<Check />} text="Web und Mobile" />
          </div>
        </div>
        <p className="relative text-xs text-blue-100/60">Community Project · Sicher verbunden über OpenID Connect</p>
      </section>

      <section className="flex items-center justify-center px-5 py-[max(2rem,env(safe-area-inset-top))] lg:bg-surface-raised lg:px-12">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center justify-center gap-3 lg:hidden"><BrandLogo className="h-20 w-20" /><span className="text-2xl font-black text-ink">buma</span></div>
          <p className="text-sm font-bold text-brand">Willkommen zurück</p>
          <h2 className="mt-2 text-3xl font-black tracking-tight text-ink">Bei buma anmelden</h2>
          <p className="mt-3 text-sm leading-6 text-ink-muted">Mit deinem Unternehmenskonto.</p>

          {microsoftIdp ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void start(() => auth.signInWithIdentityProvider(microsoftIdp))}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-xl border border-line bg-surface px-4 py-3 text-sm font-semibold text-ink transition hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-50"
              >
                <MicrosoftLogo />
                Mit Microsoft anmelden
              </button>
              <div className="mt-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-ink-muted" aria-hidden="true">
                <span className="h-px flex-1 bg-line" />oder<span className="h-px flex-1 bg-line" />
              </div>
            </>
          ) : null}

          <form onSubmit={submit} className={microsoftIdp ? "mt-6 space-y-5" : "mt-8 space-y-5"}>
            <label className="block text-sm font-semibold text-ink">E-Mail-Adresse
              <input type="email" required autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@unternehmen.de" className={INPUT} />
            </label>
            <div>
              <div className="flex items-center justify-between text-sm font-semibold">
                <label htmlFor={passwordId} className="text-ink">Passwort</label>
                <button type="button" disabled={busy} onClick={() => void start(auth.resetPassword)} className="text-brand hover:underline disabled:opacity-50">Passwort vergessen?</button>
              </div>
              <input id={passwordId} type="password" required autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} className={INPUT} />
            </div>
            <PrimaryButton className="w-full justify-center py-3" disabled={busy}>
              {busy ? "Anmeldung läuft …" : "Anmelden"}
            </PrimaryButton>
          </form>

          {error ? <p role="alert" className="mt-4 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">{error}</p> : null}
          <div className="mt-7 border-t border-line pt-6 text-center">
            <p className="text-sm text-ink-muted">Noch kein Unternehmenskonto?</p>
            <button type="button" disabled={busy} onClick={() => void start(auth.register)} className="mt-2 font-semibold text-brand hover:underline disabled:opacity-50">Account erstellen</button>
          </div>
        </div>
      </section>
    </main>
  );
}

const INPUT =
  "mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-base text-ink outline-none transition focus:border-brand focus:ring-4 focus:ring-brand/10";

/** Das Microsoft-Logo, wie es Microsoft für Anmelde-Buttons vorgibt. */
function MicrosoftLogo(): JSX.Element {
  return (
    <svg viewBox="0 0 21 21" className="h-5 w-5" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function Benefit({ icon, text }: { icon: JSX.Element; text: string }): JSX.Element {
  return <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3 text-sm font-semibold text-blue-50 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-blue-300">{icon}{text}</div>;
}
